/**
 * "This person has left — which machines do they still hold?" — a question, answered
 * by the server, with **no action attached**.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THIS PANEL HAS NO REVOKE BUTTON, AND THAT IS STILL THE POINT.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * ── What the server does on an offboard, as of Tier-3 U6 ────────────────────
 *
 * It revokes. `src/modules/devices/offboard-job.ts` handles the
 * `suggest_device_revoke` the achau sweep enqueues per offboarded employee: every
 * device that person holds moves to `revoked`, every credential of each is closed
 * (including one inside its rotation grace), and each machine gets one
 * `wp_audit_log` row carrying that sync run's correlation id. A job that outlives a
 * reinstatement re-reads `offboarded_at` and touches nothing.
 *
 * This docblock and the panel's copy used to say the opposite — that Task 20
 * deliberately registered no handler and that the jobs died unread in `wp_jobs` —
 * and that was true right up until U6 registered one. Two things follow, and the
 * second is why this panel survives unchanged:
 *
 *  1. **Nobody should read this screen as "nothing is revoking anything".** An
 *     operator who believed the old copy would think a leaver's fleet was still live
 *     and go revoking by hand, or worse, not bother. The copy now describes the
 *     revoke that actually happens.
 *  2. **The panel is now a place to *check* that it happened**, which is a better
 *     job than the one it had. `GET /devices` publishes each machine's `status`, so
 *     a leaver's machines read `revoked` here after the sweep — and a machine that
 *     does not is the interesting one.
 *
 * **No HTTP endpoint reads `wp_jobs`**, then or now, so a browser still cannot list
 * the jobs themselves; it can only see their effect on the devices.
 *
 * ── What the browser still cannot observe ───────────────────────────────────
 *
 * Who has left. `GET /devices` LEFT JOINs `wp_employees` and publishes
 * `achau_user_id` and nothing else about the person — no `is_active`, no
 * `offboarded_at`. Every place the server *does* distinguish an offboarded employee
 * it answers `404 WORKFORCE_EMPLOYEE_NOT_FOUND`, identically to "no such employee"
 * and "an employee of another company", precisely so that the difference cannot be
 * enumerated from outside.
 *
 * So this panel does not compute an accusation from data it does not have. It takes
 * an achau employee id **the operator types**, re-issues `GET /devices?employee_id=…`
 * — a real server-side filter, intersected with the caller's resolved scope — and
 * lists what comes back. The copy states, in the panel, that this answers "which
 * machines does this person hold" and **not** "this person has left", because the
 * screen cannot observe the second.
 *
 * ── And still no button ─────────────────────────────────────────────────────
 *
 * Each machine is a link into the detail, where revoking has to pass its own two
 * steps. The automatic revoke covers the case where somebody has left, which is
 * exactly the case a person does not need to adjudicate; a button *here* would be
 * for machines of people who have **not** left, on a screen that cannot tell the two
 * apart — a list turned into an instruction, aimed by an operator's typing. That was
 * the argument before U6 and it does not depend on the handler's existence.
 */

import { useState, type ReactNode, type SyntheticEvent } from 'react';

import { useI18n } from '../../i18n';
import { EmployeePicker, type EmployeePickerValue } from '../../components/EmployeePicker';
import { StatusNote } from '../../states/StatusNote';

export function OffboardAdvice({
  employeeId,
  onLookup,
}: {
  /** The employee filter currently applied to the fleet list, or `null`. */
  readonly employeeId: string | null;
  readonly onLookup: (employeeId: string | null) => void;
}): ReactNode {
  const { t } = useI18n();
  const [employee, setEmployee] = useState<EmployeePickerValue | null>(null);

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    onLookup(employee?.achau_user_id ?? null);
  };

  // The applied filter is an achau id; the name is what the picker knows about it.
  // When the two disagree — a filter restored from the URL, nothing picked yet — the
  // sentence falls back to the id rather than naming the wrong person.
  const applied =
    employee !== null && employee.achau_user_id === employeeId ? employee.full_name : employeeId;

  return (
    <StatusNote severity="info" title={t('devices.offboard.title')} testId="k5-offboard">
      <p>{t('devices.offboard.body')}</p>
      <p data-testid="k5-offboard-cannot-observe">{t('devices.offboard.cannotObserve')}</p>

      <form onSubmit={submit}>
        {/* The operator's typing is still what aims this lookup — the paragraph above
            is about exactly that — but they aim it by name now, not by an achau id
            they had to fetch from somewhere else (Luật Không In Mã Máy, R3).
            `status="all"`: the person this asks about has usually left, and the
            picker's own default hides offboarded rows. */}
        <div className="wp-field">
          <EmployeePicker
            label={t('devices.offboard.input')}
            testId="k5-offboard-input"
            status="all"
            value={employee}
            onChange={setEmployee}
          />
          <span className="wp-small wp-muted">{t('devices.employeePickHint')}</span>
        </div>
        <button type="submit" className="wp-button" data-testid="k5-offboard-apply">
          {t('devices.offboard.apply')}
        </button>
      </form>

      {employeeId === null ? null : (
        <p data-testid="k5-offboard-result">
          {t('devices.offboard.result', { employee: applied ?? employeeId })}
        </p>
      )}

      <p className="wp-small wp-muted" data-testid="k5-offboard-no-button">
        {t('devices.offboard.noButton')}
      </p>
    </StatusNote>
  );
}
